/**
 * Raw feeds behind a fused track (ENG-342): a circle where each feed that makes
 * up the track last reported the aircraft, with a dashed line to the aircraft's
 * fused position. Drawn for the SELECTED aircraft only, as in Brayden's fused
 * traffic viewer — for all ~160 they would pile up under the symbols. Surveillance
 * tab only; the Drones tab shows just the fused aircraft.
 */
import { SERVICE_COLOR } from '@/lib/map/layers/surveillance';
import { visibleTargets } from '@/lib/map/targetVisibility';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';
import { FUSED_SUMMARY_FRESH_MS } from '@/lib/surv/fusedSummary';
import type { GeoJSONSource, Map } from 'mapbox-gl';

const SRC = 'src-fused-sources';
const LINE_LAYER = 'lyr-fused-source-lines';
const DOT_LAYER = 'lyr-fused-source-dots';

/** The raw feeds that make up a fused track, and the colour of each. */
const FEED_COLOR: Record<string, string> = {
  live_ads: SERVICE_COLOR.live_ads ?? '#16a34a',
  live_mlat: SERVICE_COLOR.live_mlat ?? '#2563eb',
  live_community: '#f59e0b',
};
const FALLBACK_COLOR = '#6b7280';

export const feedColor = (serviceId: string): string => FEED_COLOR[serviceId] ?? FALLBACK_COLOR;

/** Circles and lines for the selected aircraft, as one GeoJSON collection. */
export function fusedSourceFeatures(): GeoJSON.Feature[] {
  if (useUiStore.getState().activeContext !== 'surv') return [];
  const features: GeoJSON.Feature[] = [];
  const now = Date.now();
  const selectedId = useTargetsStore.getState().selectedId;
  if (!selectedId) return [];
  for (const t of visibleTargets()) {
    if (t.target_id !== selectedId) continue;
    // A fused track that is no longer being refreshed lingers in the store for
    // a while; its circles would be stale positions.
    if (now - Date.parse(t.received_at) > FUSED_SUMMARY_FRESH_MS) continue;
    for (const s of t.sources ?? []) {
      const color = feedColor(s.service_id);
      const props = { color, service_id: s.service_id, target_id: t.target_id };
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [s.lng, s.lat],
            [t.lng, t.lat],
          ],
        },
        properties: { ...props, kind: 'line' },
      });
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        properties: { ...props, kind: 'dot' },
      });
    }
  }
  return features;
}

let lastWasEmpty = false;

export function syncFusedSourcesLayer(map: Map) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const features = fusedSourceFeatures();
  if (features.length === 0 && lastWasEmpty) return;
  lastWasEmpty = features.length === 0;
  src.setData({ type: 'FeatureCollection', features });
}

export function subscribeFusedSourcesLayer(map: Map): () => void {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'kind'], 'line'],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.85,
        'line-dasharray': [2, 2],
      },
    });
  }
  if (!map.getLayer(DOT_LAYER)) {
    map.addLayer({
      id: DOT_LAYER,
      type: 'circle',
      source: SRC,
      filter: ['==', ['get', 'kind'], 'dot'],
      paint: {
        'circle-radius': 4.5,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.25,
        'circle-opacity': 0.95,
      },
    });
  }
  lastWasEmpty = false;
  syncFusedSourcesLayer(map);
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncFusedSourcesLayer(map);
    });
  };
  const unsubT = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubSel = useTargetsStore.subscribe((s) => s.selectedId, schedule);
  const unsubUi = useUiStore.subscribe((s) => s.activeContext, schedule);
  const unsubSurv = useSurvStore.subscribe(
    (s) => `${s.mode}|${s.selectedNetwork}|${s.fusedService ? 1 : 0}|${s.fusedSelected.join(',')}`,
    schedule,
  );
  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    unsubT();
    unsubSel();
    unsubUi();
    unsubSurv();
    if (map.getLayer(LINE_LAYER)) map.removeLayer(LINE_LAYER);
    if (map.getLayer(DOT_LAYER)) map.removeLayer(DOT_LAYER);
    if (map.getSource(SRC)) map.removeSource(SRC);
  };
}
